import type { FC, SVGProps } from 'react';
import { Text } from '@upright/ui/common/generic/components/typography';
import ArrowRightIcon from '@upright/ui/icons/arrow-right';

import styles from './ActionCard.module.css';

export type Props = {
  /**
   * Imperative, and short enough to hold one line: it truncates rather than wrapping, so a row of
   * cards keeps its previews on a shared baseline.
   */
  title: string;
  /**
   * The question the card asks on the reader's behalf, in their own first person. Clamped at two
   * lines, and carried in full by the card's `title` attribute.
   */
  preview: string;
  /** The topic icon for the lilac chip, rendered at 20px inside the 40px chip. */
  icon: FC<SVGProps<SVGSVGElement>>;
  onClick: () => void;
};

/**
 * A whole-card click target that launches something. `design/50-components.md` "Card" is the spec
 * this implements, and the `/dashboard/` suggested prompts are its shipped exemplar.
 *
 * Promoted from three private reimplementations that had each drifted (PLT-6526): the
 * module-private card in `chat/suggested-prompt-cards.tsx`, the prototype card in
 * `views/internal/proto/proto-cards.tsx`, and the story-local one under `Design System/Card`. The
 * three copies of one spec disagreed on four things: whether the row centres on the chip, whether
 * the title-to-preview step is `xs` or `sm`, whether keyboard focus gets the lift or only the ring,
 * and how the two lines of text truncate. That is the argument for a component rather than a fourth
 * copy.
 *
 * **A launcher, not a link to a page.** The icon chip, the trailing arrow and the lift are all
 * promises that something starts when it is pressed, so a block of content in a product surface is a
 * `DataCard` or a plain section whatever its shape. It takes `onClick` and no `href` for the same
 * reason: every shipped call site launches a chat turn rather than navigating, and an anchor wearing
 * this chrome would promise the wrong thing.
 *
 * **The title truncates to one line and the preview clamps at two.** That is the landed rule
 * (DESIGN.md rule 27 and `design/50-components.md` "Truncation"), and it is the opposite way round
 * from the shipped dashboard card, which wrapped the title and clipped the preview to one line. The
 * shipped card's argument for wrapping is worth keeping in view: three-up in the dashboard content
 * column the card measures 290px and this title line 170px of it, so three of the six prompt titles
 * really do truncate, and "Assess physical climate ri…" is the one thing on the card a reader cannot
 * do without. Two things settle it the other way. The row: a title that wraps in one card and not in
 * its neighbour puts their previews at different heights, and `Text`'s `truncate` keeps the full
 * string reachable in a native `title`. And the count, measured on both sides in the browser:
 * clipping the title clips three of the six strings, where clipping the preview to one line clipped
 * all six, at 231-335px of text into that same 170px. If the clipped titles turn out to read worse
 * than the ragged row, this is one line to change here, not a prop: per-call-site truncation is how
 * the three copies came to disagree in the first place.
 */
export function ActionCard(props: Props) {
  const { title, preview, icon: Icon, onClick } = props;

  return (
    <button type="button" className={styles.root} title={preview} onClick={onClick}>
      <span className={styles.chip}>
        <Icon width={20} height={20} />
      </span>
      {/*
       * Both lines set their leading, because the page inherits `body { line-height: 1.5 }`
       * unitless: a size without its paired `height` re-multiplies 1.5 by its own font size and
       * reserves 21px for a 14px line where the pairing asks for 20px.
       */}
      <span className={styles.text}>
        <Text size="md" height="sm" weight="bold" color="gray-900" truncate>
          {title}
        </Text>
        {/*
         * `textWrap="pretty"` has to be asked for here. `reset.css` grants it to every `<p>`, and
         * this line is a `<span>` inside a `<button>`, so the preview was the one piece of prose on
         * the card laying its lines out greedily. Two lines is where that shows worst: a full first
         * line and two words under it is the shape it falls into, and the clamp means there is no
         * third line for the eye to recover on. `pretty` and not `balance` — `balance` equalises
         * every line of the block, including the ones the clamp hides.
         */}
        <Text size="sm" height="xs" color="gray-600" textWrap="pretty" asChild>
          <span className={styles.preview}>{preview}</span>
        </Text>
      </span>
      {/*
       * The icon component, never a typed arrow: a glyph doing an affordance's job takes the
       * surrounding text's weight and baseline instead of the icon rhythm, which
       * design/10-foundations.md "Icons" calls a defect rather than a shortcut. `aria-hidden`
       * because the card's own label already says where it goes.
       */}
      <span className={styles.arrow} aria-hidden="true">
        <ArrowRightIcon width={18} height={18} />
      </span>
    </button>
  );
}
